/**
 * Iter XX.267 — Gully Pro Waitlist Sheet.
 *
 * Two-stage flow:
 *   1. Confirm success — "You're on the Gully Pro waitlist."
 *   2. Optional 1-question intent — "Would you seriously consider
 *      Gully Pro at $99/month?" (Yes / Maybe / No).
 *
 * Design goals: friction-light, mobile-native, matches XX.266b design
 * system (dark neutral + restrained emerald accent).
 *
 * Analytics (additive events only, via /plus/track):
 *   · pro_waitlist_joined                 — fired once on successful join
 *   · pro_price_intent_yes/maybe/no       — fired when the intent is set
 *
 * Never touches billing / entitlements. Failures never block the UX.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { joinProWaitlist, trackProEvent } from "../lib/proWaitlist";
// Iter XX.267b — success haptic on the confirmed join, light haptic
// on the optional Yes/Maybe/No intent selection.
import { hapticLight, hapticSuccess } from "../lib/haptics";

export default function ProWaitlistSheet({
  source = "upgrade_page",
  sessionId = null,
  onClose,
}) {
  const [phase, setPhase] = useState("joining"); // joining | success | thanks | error
  const [intentPending, setIntentPending] = useState(false);
  const entryIdRef = useRef(null);
  const joinedRef = useRef(false);

  useEffect(() => {
    if (joinedRef.current) return;
    joinedRef.current = true;
    (async () => {
      const r = await joinProWaitlist({ source, sessionId });
      if (r?.ok) {
        entryIdRef.current = r.entry_id || null;
        // Iter XX.267b — success notification haptic when the join
        // lands. Never fires on error / on repeat mounts because the
        // outer `joinedRef` gate limits this branch to one execution.
        hapticSuccess();
        setPhase("success");
        trackProEvent("pro_waitlist_joined", source, sessionId, {
          fresh_join: !!r.joined,
        });
      } else {
        setPhase("error");
      }
    })();
  }, [source, sessionId]);

  async function submitIntent(value) {
    if (intentPending) return;
    setIntentPending(true);
    // Iter XX.267b — light haptic on the intent chip tap.
    hapticLight();
    trackProEvent(`pro_price_intent_${value}`, source, sessionId);
    await joinProWaitlist({
      source, sessionId, entryId: entryIdRef.current, intentResponse: value,
    });
    setPhase("thanks");
    setIntentPending(false);
  }

  return (
    <div
      data-testid="pro-waitlist-sheet"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-[#0b0b0b] border border-neutral-800 rounded-t-3xl sm:rounded-3xl px-6 pt-7 pb-6"
        style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="pro-waitlist-close"
          className="absolute top-4 right-4 p-1 text-neutral-500 hover:text-neutral-300"
        >
          <X size={16} />
        </button>

        {phase === "joining" && (
          <div className="py-8 text-center" data-testid="pro-waitlist-joining">
            <div className="inline-block w-6 h-6 rounded-full border-2 border-neutral-700 border-t-emerald-400 animate-spin mb-3" />
            <p className="text-sm text-neutral-400">Adding you to the Pro waitlist…</p>
          </div>
        )}

        {phase === "error" && (
          <div className="py-2" data-testid="pro-waitlist-error">
            <h3 className="font-display text-xl text-neutral-100 mb-2">
              Something went wrong
            </h3>
            <p className="text-sm text-neutral-400 leading-relaxed mb-5">
              We couldn&apos;t add you to the waitlist right now. Try again
              in a moment.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="pro-waitlist-error-close"
              className="w-full py-3 rounded-xl border border-neutral-700 text-neutral-100 text-sm hover:bg-neutral-900 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {phase === "success" && (
          <div data-testid="pro-waitlist-success">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-950/60 border border-emerald-900/60 text-emerald-400 mb-4">
              <CheckCircle2 size={22} strokeWidth={1.5} />
            </div>
            <h3
              data-testid="pro-waitlist-success-headline"
              className="font-display text-2xl text-neutral-50 leading-tight mb-2"
            >
              You&apos;re on the Gully Pro waitlist.
            </h3>
            <p className="text-sm text-neutral-400 leading-relaxed mb-6">
              We&apos;ll let you know the moment Pro is ready. In the
              meantime — one quick question.
            </p>

            <div
              data-testid="pro-waitlist-intent"
              className="rounded-2xl border border-neutral-800 bg-neutral-950/60 p-4 mb-4"
            >
              <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
                Price check
              </p>
              <p className="text-sm text-neutral-200 leading-relaxed mb-4">
                Would you seriously consider Gully Pro at{" "}
                <span className="text-neutral-50 font-medium">$99 / month</span>?
              </p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  disabled={intentPending}
                  onClick={() => submitIntent("yes")}
                  data-testid="pro-waitlist-intent-yes"
                  className="py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors disabled:opacity-60"
                >
                  Yes
                </button>
                <button
                  type="button"
                  disabled={intentPending}
                  onClick={() => submitIntent("maybe")}
                  data-testid="pro-waitlist-intent-maybe"
                  className="py-2.5 rounded-lg border border-neutral-700 text-neutral-100 text-sm hover:bg-neutral-900 transition-colors disabled:opacity-60"
                >
                  Maybe
                </button>
                <button
                  type="button"
                  disabled={intentPending}
                  onClick={() => submitIntent("no")}
                  data-testid="pro-waitlist-intent-no"
                  className="py-2.5 rounded-lg border border-neutral-700 text-neutral-100 text-sm hover:bg-neutral-900 transition-colors disabled:opacity-60"
                >
                  No
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              data-testid="pro-waitlist-skip"
              className="w-full py-2.5 text-neutral-500 hover:text-neutral-300 text-sm transition-colors"
            >
              Skip
            </button>
          </div>
        )}

        {phase === "thanks" && (
          <div data-testid="pro-waitlist-thanks">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-950/60 border border-emerald-900/60 text-emerald-400 mb-4">
              <CheckCircle2 size={22} strokeWidth={1.5} />
            </div>
            <h3
              data-testid="pro-waitlist-thanks-headline"
              className="font-display text-2xl text-neutral-50 leading-tight mb-2"
            >
              Thanks — got it.
            </h3>
            <p className="text-sm text-neutral-400 leading-relaxed mb-6">
              Your answer helps us shape Gully Pro. We&apos;ll be in touch
              when it&apos;s ready.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="pro-waitlist-thanks-close"
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
